/**
 * <BarcodeScannerOverlay> — TRUE-fullscreen camera scanner used by
 * invoice / draft / quotation / sale flows. Rendered via React portal
 * into <body> so it ALWAYS escapes any transformed / filtered ancestor.
 *
 * Layout (top → bottom):
 *   ┌───────────────────────────────────────┐
 *   │  Scan product barcode         (title) │
 *   │  [SKU × 6] [SKU × 12] [SKU × 6]       │  ← Recent scans chips
 *   │                                       │
 *   │            CAMERA + RETICLE           │
 *   │                                       │
 *   │            [Order summary]            │  ← Collapsible summary
 *   │        [Close scanner]  (pill)        │
 *   └───────────────────────────────────────┘
 *   ✕ top-right                (56×56)
 *   ⚡ top-right, below ✕      (torch, if supported)
 *
 * Features (Jul 2026):
 *  - createPortal to <body> — escapes transformed ancestors
 *  - Beep on detect (Web Audio, 880Hz sine, 120ms)
 *  - Torch toggle — polls `getCapabilities().torch` for up to 2s after
 *    the video starts playing, because Chrome/Samsung populates the
 *    capability only after the track has produced frames (real-device
 *    bug found on S24 Ultra, Jul 2026)
 *  - Recent scans strip — chips of the last 3 adds; tap to undo
 *  - Live invoice summary — Products / Qty / Subtotal / VAT / Total
 *    with a collapse toggle so smaller phones can reclaim camera space
 *  - Two close affordances: 56×56 ✕ top-right + wide pill bottom-center
 *  - 100dvh × 100vw fill, safe-area padded, body scroll locked
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
    X, ScanLine, Zap, ZapOff, Undo2, ChevronDown, ChevronUp,
    ShoppingCart, Boxes,
} from 'lucide-react';
import { BrowserMultiFormatReader } from '@zxing/browser';

// Short 880Hz "beep" via Web Audio API — no bundled assets, autoplay-safe.
const playBeep = () => {
    try {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return;
        const ctx = new AC();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = 880;
        gain.gain.setValueAtTime(0.001, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.12);
        osc.connect(gain).connect(ctx.destination);
        osc.start();
        osc.stop(ctx.currentTime + 0.13);
        setTimeout(() => ctx.close().catch(() => {}), 300);
    } catch { /* audio is nice-to-have */ }
};

// Poll `getCapabilities().torch` for up to `timeoutMs`. Chrome/Samsung
// only exposes torch AFTER the video track has started producing
// frames, so a naïve check on the immediate promise resolution misses
// it. We retry every 200ms and bail as soon as we see it (or timeout).
const detectTorchSupport = (track, timeoutMs = 2000) => new Promise((resolve) => {
    const started = Date.now();
    const tick = () => {
        try {
            const caps = track?.getCapabilities?.();
            if (caps && 'torch' in caps) {
                resolve(true);
                return;
            }
        } catch { /* ignore */ }
        if (Date.now() - started >= timeoutMs) {
            resolve(false);
            return;
        }
        setTimeout(tick, 200);
    };
    tick();
});

const BarcodeScannerOverlay = ({
    open, onDetected, onClose,
    recentScans = [], onUndoScan,
    summary,
}) => {
    const videoRef = useRef(null);
    const controlsRef = useRef(null);
    const streamRef = useRef(null);
    const lastCodeRef = useRef({ code: null, at: 0 });
    const [error, setError] = useState(null);
    const [status, setStatus] = useState('idle');
    const [torchSupported, setTorchSupported] = useState(false);
    const [torchOn, setTorchOn] = useState(false);
    const [summaryOpen, setSummaryOpen] = useState(true);

    // Body scroll lock
    useEffect(() => {
        if (!open) return undefined;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = prev; };
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;

        const reader = new BrowserMultiFormatReader();
        setStatus('starting');
        setError(null);
        setTorchSupported(false);
        setTorchOn(false);

        let cancelled = false;

        reader
            .decodeFromConstraints(
                {
                    audio: false,
                    video: {
                        facingMode: { ideal: 'environment' },
                        width: { ideal: 1280 },
                        height: { ideal: 720 },
                    },
                },
                videoRef.current,
                (result) => {
                    if (cancelled || !result) return;
                    setStatus('scanning');
                    const code = result.getText().trim();
                    if (!code) return;
                    const now = Date.now();
                    if (lastCodeRef.current.code === code &&
                        now - lastCodeRef.current.at < 1_500) {
                        return;
                    }
                    lastCodeRef.current = { code, at: now };
                    playBeep();
                    onDetected(code);
                },
            )
            .then((controls) => {
                if (cancelled) {
                    controls?.stop();
                    return;
                }
                controlsRef.current = controls;
                setStatus('scanning');
                const stream = videoRef.current?.srcObject;
                if (stream && stream.getVideoTracks) {
                    streamRef.current = stream;
                    const track = stream.getVideoTracks()[0];
                    // Poll for torch capability — critical on Samsung.
                    detectTorchSupport(track).then((supported) => {
                        if (!cancelled) setTorchSupported(supported);
                    });
                }
            })
            .catch((e) => {
                if (cancelled) return;
                setError(e?.message || 'Camera unavailable');
                setStatus('error');
            });

        return () => {
            cancelled = true;
            try { controlsRef.current?.stop(); } catch { /* ignore */ }
            controlsRef.current = null;
            streamRef.current = null;
        };
    }, [open, onDetected]);

    const toggleTorch = useCallback(async () => {
        const stream = streamRef.current;
        if (!stream) return;
        const track = stream.getVideoTracks()[0];
        if (!track) return;
        const next = !torchOn;
        try {
            await track.applyConstraints({ advanced: [{ torch: next }] });
            setTorchOn(next);
        } catch (e) {
            setTorchSupported(false);
            console.warn('Torch toggle failed:', e?.message);
        }
    }, [torchOn]);

    if (!open) return null;

    const overlayStyle = { height: '100dvh', width: '100vw' };

    // Currency formatter fallback — the parent normally passes one.
    const fmt = summary?.currencyFmt || ((v) => `€${(Number(v) || 0).toFixed(2)}`);

    const overlay = (
        <div
            className="fixed inset-0 z-[9999] bg-black flex flex-col overflow-hidden"
            style={overlayStyle}
            data-testid="barcode-scanner-overlay"
            role="dialog"
            aria-modal="true"
            aria-label="Barcode scanner"
        >
            <div
                className="flex items-center gap-2 px-4 py-3 text-white bg-black/70"
                style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)' }}
            >
                <ScanLine size={18} className="text-[#C08478]" />
                <span className="text-sm font-medium">Scan product barcode</span>
            </div>

            <div className="flex-1 relative overflow-hidden">
                <video
                    ref={videoRef}
                    className="w-full h-full object-cover"
                    playsInline
                    muted
                    autoPlay
                    data-testid="scanner-video"
                />

                {/* Recent scans strip */}
                {recentScans.length > 0 && (
                    <div
                        data-testid="recent-scans-strip"
                        className="absolute left-0 right-0 flex gap-2 overflow-x-auto no-scrollbar px-3 py-2 z-10"
                        style={{ top: 'calc(env(safe-area-inset-top, 0px) + 60px)' }}
                    >
                        {recentScans.map((r) => (
                            <button
                                key={r.id}
                                type="button"
                                onClick={() => onUndoScan?.(r)}
                                disabled={!onUndoScan}
                                data-testid={`recent-scan-${r.product.id}`}
                                className="shrink-0 flex items-center gap-2 pl-3 pr-3 h-9 rounded-full bg-black/70 hover:bg-black/85 active:bg-black text-white text-xs font-medium border border-white/30 backdrop-blur transition-colors disabled:opacity-60"
                                title={onUndoScan ? 'Tap to undo this scan' : ''}
                            >
                                <Undo2 size={14} className="text-[#C08478] shrink-0" />
                                <span className="font-mono">{r.product.sku}</span>
                                <span className="opacity-80">× {r.qty}</span>
                            </button>
                        ))}
                    </div>
                )}

                <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                    <div className="w-4/5 max-w-lg h-40 border-2 border-[#C08478] rounded-lg shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]">
                        <div className="w-full h-full relative overflow-hidden">
                            <div className="absolute left-0 right-0 h-[2px] bg-[#C08478] animate-[scan_1.8s_linear_infinite]" />
                        </div>
                    </div>
                </div>

                {/* Torch toggle (only when supported) */}
                {torchSupported && (
                    <button
                        type="button"
                        onClick={toggleTorch}
                        aria-label={torchOn ? 'Turn flashlight off' : 'Turn flashlight on'}
                        data-testid="scanner-torch-btn"
                        className={`absolute w-14 h-14 rounded-full backdrop-blur border border-white/40 shadow-2xl flex items-center justify-center transition-colors ${
                            torchOn
                                ? 'bg-[#E9C46A] text-[#1A1A1A] hover:bg-[#D9B458]'
                                : 'bg-black/70 hover:bg-black/90 text-white'
                        }`}
                        style={{
                            top: 'calc(env(safe-area-inset-top, 0px) + 84px)',
                            right: 'calc(env(safe-area-inset-right, 0px) + 12px)',
                        }}
                    >
                        {torchOn ? <Zap size={26} strokeWidth={2.5} /> : <ZapOff size={26} strokeWidth={2.5} />}
                    </button>
                )}

                {/* Fat 56×56 ✕ top-right */}
                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close scanner"
                    data-testid="scanner-close-btn"
                    className="absolute w-14 h-14 rounded-full bg-black/70 backdrop-blur hover:bg-black/90 active:bg-black text-white flex items-center justify-center shadow-2xl border border-white/40 transition-colors"
                    style={{
                        top: 'calc(env(safe-area-inset-top, 0px) + 12px)',
                        right: 'calc(env(safe-area-inset-right, 0px) + 12px)',
                    }}
                >
                    <X size={30} strokeWidth={2.5} />
                </button>

                {/* Bottom stack: summary → status → close pill.
                    Everything except the close pill lives INSIDE this
                    column so the pill can stay pinned to the safe
                    area regardless of how tall the summary gets. */}
                {summary && (
                    <SummaryPanel
                        summary={summary}
                        fmt={fmt}
                        open={summaryOpen}
                        onToggle={() => setSummaryOpen((v) => !v)}
                    />
                )}

                <div
                    className="absolute left-0 right-0 text-center text-white/90 text-xs px-6 pointer-events-none"
                    style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 84px)' }}
                >
                    {status === 'starting' && 'Starting camera…'}
                    {status === 'scanning' && !summary && 'Point at a barcode — auto-detects EAN / UPC / Code128'}
                    {status === 'error' && (
                        <span className="text-[#E76F51]">
                            {error} — please allow camera access and try again.
                        </span>
                    )}
                </div>

                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close scanner"
                    data-testid="scanner-close-pill"
                    className="absolute left-1/2 -translate-x-1/2 h-12 px-6 rounded-full bg-white/95 hover:bg-white text-[#1A1A1A] font-medium text-sm shadow-2xl border border-white/60 flex items-center gap-2 active:scale-95 transition-transform"
                    style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 20px)' }}
                >
                    <X size={16} strokeWidth={2.5} />
                    Close scanner
                </button>
            </div>

            <style>{`
                @keyframes scan {
                    0%   { top: 0%; }
                    50%  { top: 100%; }
                    100% { top: 0%; }
                }
                .no-scrollbar::-webkit-scrollbar { display: none; }
                .no-scrollbar { scrollbar-width: none; }
            `}</style>
        </div>
    );

    return createPortal(overlay, document.body);
};

/**
 * Collapsible live invoice summary — sits just above the close pill.
 * Two states:
 *  - Expanded (default): 4-metric grid (Products, Qty, Subtotal, VAT)
 *    plus the Total on a highlighted row.
 *  - Collapsed: single line "▼ €1,248.50 · 18 products" — reclaims
 *    camera space on smaller phones.
 * Tap anywhere in the header row to toggle.
 * Info-only — no save/finalize actions per spec.
 */
const SummaryPanel = ({ summary, fmt, open, onToggle }) => {
    const {
        productCount = 0,
        totalQty = 0,
        subtotal = 0,
        vatAmount = 0,
        total = 0,
    } = summary;

    return (
        <div
            data-testid="scanner-summary-panel"
            className="absolute left-3 right-3 z-10 bg-black/75 backdrop-blur-md rounded-2xl border border-white/20 shadow-2xl text-white overflow-hidden"
            style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 72px)' }}
        >
            <button
                type="button"
                onClick={onToggle}
                data-testid="scanner-summary-toggle"
                className="w-full flex items-center justify-between gap-3 px-4 py-2.5 hover:bg-white/5 active:bg-white/10 transition-colors"
                aria-expanded={open}
            >
                {open ? (
                    <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-white/70">
                        <ChevronDown size={14} /> Order summary
                    </span>
                ) : (
                    <span className="flex items-center gap-3 text-sm">
                        <ChevronUp size={14} className="text-white/70" />
                        <span className="font-mono font-bold text-[#C08478]" data-testid="summary-collapsed-total">
                            {fmt(total)}
                        </span>
                        <span className="text-white/70 text-xs">
                            · {productCount} {productCount === 1 ? 'product' : 'products'}
                        </span>
                    </span>
                )}
                <span className="text-[10px] text-white/50 uppercase tracking-wider">
                    {open ? 'tap to hide' : 'tap to expand'}
                </span>
            </button>

            {open && (
                <div className="px-4 pb-3 pt-1">
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                        <MetricRow
                            icon={<ShoppingCart size={12} className="text-[#C08478]" />}
                            label="Products"
                            value={productCount}
                            testId="summary-products"
                        />
                        <MetricRow
                            icon={<Boxes size={12} className="text-[#C08478]" />}
                            label="Qty"
                            value={totalQty}
                            testId="summary-qty"
                        />
                        <MetricRow
                            label="Subtotal"
                            value={fmt(subtotal)}
                            mono
                            testId="summary-subtotal"
                        />
                        <MetricRow
                            label="VAT"
                            value={fmt(vatAmount)}
                            mono
                            testId="summary-vat"
                        />
                    </div>
                    <div className="mt-2 pt-2 border-t border-white/15 flex items-baseline justify-between">
                        <span className="text-xs uppercase tracking-wider text-white/70">Total</span>
                        <span
                            className="font-mono font-bold text-lg text-[#C08478]"
                            data-testid="summary-total"
                        >
                            {fmt(total)}
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
};

const MetricRow = ({ icon, label, value, mono, testId }) => (
    <div className="flex items-center justify-between gap-2 min-w-0">
        <span className="flex items-center gap-1.5 text-xs text-white/70">
            {icon}{label}
        </span>
        <span
            className={`text-sm ${mono ? 'font-mono' : 'font-semibold'} text-white truncate`}
            data-testid={testId}
        >
            {value}
        </span>
    </div>
);

export default BarcodeScannerOverlay;
